import React, { useEffect } from 'react';
import { Center, Heading, Image, Text, useToast } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import chargerImg from '../../../assets/images/evse_charger_fail.svg';
import { ContactSupportButton } from '../../../common/components/ContactSupportButton';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { trackUserOfflineSetupError } from '../../../utils/common/analytics';
import { trackDynatraceUserOfflineSetupError } from '../../../utils/native/dynatrace-events';
import { resetWifiConfig } from '../wifi-configuration/wifiConfigSlice';
import { selectDeviceSetupState } from './deviceSetupSlice';

type Props = {
  title?: string;
  description?: string;
};

/* istanbul ignore next -- @preserve */
export function SetupFail({ title, description }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const backURL = '../../home/setup-fail';
  const path = `../../account/support?direction=back&backURL=${backURL}`;
  const { isLoaded, status, message } = useSelector(selectDeviceSetupState);
  const toast = useToast();

  // Reached from both onboarding and Wi-Fi-edit failures — either way, this is a terminal state
  // for whatever attempt failed, so it's safe to unconditionally clear a leftover isEditing flag.
  useEffect(() => {
    dispatch(resetWifiConfig());
  }, [dispatch]);

  useEffect(() => {
    if (!isLoaded) return;

    const errorMessage = message || 'setup_failed';
    trackUserOfflineSetupError();
    trackDynatraceUserOfflineSetupError(errorMessage);

    if (status !== 'success') {
      if (message?.includes('Not support EVSE charger model')) {
        toast({ title: t('Set Up Hardware.unsupported charger'), status: 'error', isClosable: true });
      } else if (message?.includes('EV Charger is already in use')) {
        toast({ title: t('Set Up Hardware.charger is already linked'), status: 'error', isClosable: true });
      }
    }
  }, [isLoaded, status, message, toast, t]);

  return (
    <SlidingAnimationPageBase
      title={t('Common.device setup')}
      backURL="../device-scan"
      display="flex"
      flexDirection="column"
    >
      <Center flexDirection="column" px={3} textAlign="center" flex={1}>
        <Image data-testid="ev-charger-image" src={chargerImg} alt="Charging Station Setup Failed" ml={7} my={4} />

        <Heading mt={2} size={'lg'}>
          {title || t('Common.something went wrong')}
        </Heading>
        <Text data-testid="ev-setup-fail-text" mt={1}>
          {description || t('Set Up Hardware.could not connect charger')}
        </Text>

        <ContactSupportButton path={path} />
      </Center>
    </SlidingAnimationPageBase>
  );
}
